import posthog from 'posthog-js';
import { useCallback, useEffect, useRef, useState } from 'react';

interface ChatMetricsCallbacks {
  dispatchChatToggleMetric: (newIsOpen: boolean) => void;
}

const useLangflowMetrics = (): ChatMetricsCallbacks => {
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const sessionStartTimeRef = useRef<number | null>(null);
  const isOpenRef = useRef<boolean>(false);

  useEffect(() => {
    sessionStartTimeRef.current = sessionStartTime;
  }, [sessionStartTime]);

  const startChatSession = useCallback(() => {
    const startTime = Date.now();

    setSessionStartTime(startTime);
    sessionStorage.setItem('chat_session_start', startTime.toString());
    sessionStorage.setItem('chat_session_active', 'true');

    posthog.capture('chat_session_started', {
      timestamp: startTime,
      page_path: window.location.pathname,
    });
  }, []);

  const endChatSession = useCallback(
    (reason: 'user_closed' | 'page_unload' | 'tab_hidden' = 'user_closed') => {
      const currentStartTime = sessionStartTimeRef.current;

      if (currentStartTime) {
        const duration = Date.now() - currentStartTime;
        const durationSeconds = Math.floor(duration / 1000);

        posthog.capture('chat_session_ended', {
          timestamp: Date.now(),
          duration_seconds: durationSeconds,
          reason,
          ended_normally: reason === 'user_closed',
        });
      }

      // Cleanup
      setSessionStartTime(null);
      sessionStorage.removeItem('chat_session_start');
      sessionStorage.removeItem('chat_session_active');
    },
    [],
  );

  // Callback principal para toggle do chat
  const dispatchChatToggleMetric = useCallback(
    (newIsOpen: boolean) => {
      isOpenRef.current = newIsOpen;

      if (newIsOpen && !sessionStartTimeRef.current) {
        startChatSession();
      } else if (!newIsOpen && sessionStartTimeRef.current) {
        endChatSession('user_closed');
      }
    },
    [startChatSession, endChatSession],
  );

  useEffect(() => {
    // Verificar sessões interrompidas na inicialização
    const checkInterruptedSession = () => {
      const sessionStart = sessionStorage.getItem('chat_session_start');
      const wasActive = sessionStorage.getItem('chat_session_active');

      if (sessionStart && wasActive) {
        const interruptedDuration = Date.now() - parseInt(sessionStart);

        posthog.capture('chat_session_interrupted_detected', {
          timestamp: Date.now(),
          interrupted_duration: interruptedDuration,
          recovery_timestamp: Date.now(),
        });

        sessionStorage.removeItem('chat_session_start');
        sessionStorage.removeItem('chat_session_active');
      }
    };

    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      const chatIsOpen = isOpenRef.current;
      const hasStart = sessionStartTimeRef.current !== null;

      if (!isVisible && hasStart) {
        endChatSession('tab_hidden');
      } else if (isVisible && chatIsOpen && !hasStart) {
        startChatSession();
      }
    };

    // Before unload - usuário fechando site
    const handleBeforeUnload = () => {
      if (sessionStartTimeRef.current) {
        endChatSession('page_unload');
      }
    };

    // Inicialização
    checkInterruptedSession();

    // Event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [endChatSession, startChatSession]); // Dependências necessárias

  return {
    dispatchChatToggleMetric,
  };
};

export default useLangflowMetrics;
